import { BaseApiService } from './BaseApiService';
import { WorkoutHistory, MovementUnit } from '../../types';

import { CacheEntry, CacheManager } from '../cache/CacheManager';

/** A strength PR at a specific set: mirrors StrengthPrPart in the backend's workoutUtils.ts. */
export interface StrengthPrPart {
  weight: number;
  unit: string;
  reps: number;
  date: string;
  workoutId: string;
}

/** A stretching/mobility hold-time PR: mirrors HoldTimePrPart in the backend's workoutUtils.ts. */
export interface HoldTimePrPart {
  seconds: number;
  date: string;
  workoutId: string;
}

/**
 * TODO.md H4: exerciseType-aware personal record for one exercise. `maxWeight`/`maxReps` are
 * independent PRs for a `strength` exercise (never derive one from the other); `stretching`/`mobility`
 * exercises get `maxHoldTime` instead and never populate the weight/rep fields. Mirrors
 * ExercisePrRecord in the backend's workoutUtils.ts.
 */
export interface ExercisePrRecord {
  name: string;
  exerciseType: string;
  maxWeight?: StrengthPrPart;
  maxReps?: StrengthPrPart;
  maxHoldTime?: HoldTimePrPart;
}

// Max weight cache storage
const maxWeightCache = new Map<string, CacheEntry<{ maxWeight: number; unit: string; maxRepsForWeight: number; exerciseName: string; date: string; workoutId: string }>>();
const allMaxWeightsCache = new Map<string, CacheEntry<ExercisePrRecord[]>>();
const personalRecordsCache = new Map<string, CacheEntry<ExercisePrRecord[]>>();
// TODO.md H4: what ExerciseInfoModal.tsx calls - keyed by `${userId}_${exerciseName}` like
// maxWeightCache above, but a distinct map since the two endpoints/shapes are unrelated (see
// ExerciseMaxWeight's doc comment in the backend's workoutUtils.ts for why getMaxWeightForExercise's
// route couldn't just gain these fields in place).
const exercisePrCache = new Map<string, CacheEntry<ExercisePrRecord | null>>();
const progressAnalyticsCache = new Map<string, CacheEntry<{
  chartData: any[];
  topExercises: any[];
  seriesKeys: string[];
  lineData: any[];
  pieData: any[];
}>>();
// Frontend-only cache for paginated workout history
const historyCache = new Map<string, CacheEntry<WorkoutHistoryPagination>>();

/**
 * Pagination interface for workout history
 */
export interface WorkoutHistoryPagination {
  data: WorkoutHistory[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

/**
 * Workout History Service
 * Handles all workout history-related API operations with pagination
 */
export class WorkoutHistoryService extends BaseApiService {
  /**
   * GET /api/workout-history?userId={userId}&page={page}&limit={limit}
   * Fetch user's workout history with pagination
   */
  async getWorkoutHistory(
    userId: string, 
    page: number = 1, 
    limit: number = 8
  ): Promise<WorkoutHistoryPagination> {
    const params = new URLSearchParams({
      userId,
      page: page.toString(),
      limit: limit.toString()
    });

    // Frontend-only cache key (per user + page + limit)
    const cacheKey = `${userId}_${page}_${limit}`;

    // Check cache first
    const cached = CacheManager.get(historyCache, cacheKey);
    if (cached !== null) {
      console.log(`📦 Using cached workout history for: ${cacheKey}`);
      return cached;
    }

    // Cache miss or expired, fetch from API
    console.log(`🌐 Fetching fresh workout history for: ${cacheKey}`);
    const response = await this.get<WorkoutHistoryPagination>(`/workout-history?${params.toString()}`);
    
    console.log('📡 [WORKOUT SERVICE] Raw API response received:', {
      response: response,
      responseType: typeof response,
      hasData: !!response?.data,
      dataLength: response?.data?.length || 0,
      pagination: response?.pagination
    });
    
    const result = response || {
      data: [],
      pagination: {
        page: 1,
        limit: 8,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      }
    };

    console.log('📦 [WORKOUT SERVICE] Final result to be cached and returned:', {
      workoutCount: result.data?.length || 0,
      totalCount: result.pagination?.total || 0,
      workoutIds: result.data?.map(w => ({ id: w.id, date: w.date, exerciseCount: w.exerciseCount })) || []
    });

    // Cache the result
    CacheManager.set(historyCache, cacheKey, result, CacheManager.ttl.workoutHistory);

    return result;
  }

  /**
   * GET /api/workout-history?userId={userId} - Fetch all user's workout history (legacy)
   */
  async getAllWorkoutHistory(userId: string): Promise<WorkoutHistory[]> {
    const response = await this.get<{ data: WorkoutHistory[] }>(`/workout-history?userId=${userId}`);
    return response?.data || [];
  }

  /**
   * POST /api/workout-history - Save a completed workout
   * Automatically clears all caches when workout is saved
   */
  async saveWorkout(workout: Omit<WorkoutHistory, 'id' | 'date'> & { date?: string }): Promise<WorkoutHistory> {
    console.log('💪 Saving workout - clearing all caches');
    
    // Clear all caches for this user since new workout might affect all analytics
    this.clearAllCachesForUser(workout.userId);
    
    return this.post<WorkoutHistory>('/workout-history', workout);
  }

  /**
   * PUT /api/workout-history/:id - Update an existing workout
   * Clears all caches for this user because edits can affect analytics and pagination.
   */
  async updateWorkout(
    userId: string,
    workoutId: string,
    data: {
      date?: string | Date;
      notes?: string;
      durationMinutes?: number | null;
      exercises: Array<{
        id?: string;
        exerciseId?: string;
        name: string;
        muscleGroup?: string;
        completed: boolean;
        sets: Array<{ reps: number; weight: number; unit: MovementUnit }>;
      }>;
    }
  ): Promise<WorkoutHistory> {
    this.clearAllCachesForUser(userId);
    return this.put<WorkoutHistory>(`/workout-history/${workoutId}`, data);
  }

  /**
   * GET /api/workout-history/max-weights/:exerciseName?userId={userId}
   * Get max weight for a specific exercise from workout history with caching
   */
  async getMaxWeightForExercise(
    userId: string,
    exerciseName: string
  ): Promise<{ maxWeight: number; unit: string; maxRepsForWeight: number; exerciseName: string; date: string; workoutId: string }> {
    // Create cache key
    const cacheKey = `${userId}_${exerciseName}`;
    
    // Check cache first
    const cached = CacheManager.get(maxWeightCache, cacheKey);
    if (cached !== null) {
      console.log(`📦 Using cached max weight for: ${exerciseName}`);
      return cached;
    }

    // Cache miss or expired, fetch from API
    console.log(`🌐 Fetching fresh max weight for: ${exerciseName}`);
    let result: { maxWeight: number; unit: string; maxRepsForWeight: number; exerciseName: string; date: string; workoutId: string };

    try {
      result = await this.get<{ maxWeight: number; unit: string; maxRepsForWeight: number; exerciseName: string; date: string; workoutId: string }>(
        `/workout-history/max-weights/${encodeURIComponent(exerciseName)}?userId=${userId}`
      );
    } catch (error) {
      // If the exercise has no weighted history (e.g. assisted/bodyweight only), backend returns 404.
      // Fall back to a sensible default instead of surfacing an error to the UI.
      console.warn('Max weight not found, using default for exercise:', exerciseName, error);
      result = {
        maxWeight: 0,
        unit: 'kg',
        maxRepsForWeight: 0,
        exerciseName,
        date: new Date().toISOString(),
        workoutId: 'fallback',
      };
    }

    // Cache the result
    CacheManager.set(maxWeightCache, cacheKey, result, CacheManager.ttl.maxWeight);

    return result;
  }

  /**
   * GET /api/workout-history/max-weights?userId={userId}
   * Get exerciseType-aware PRs for every exercise in the user's history, with caching.
   *
   * TODO.md H4: fixes a real wrapper-mismatch bug - the backend has always returned a bare JSON array
   * (`res.json(maxWeights)`), but this method used to read `response?.data`, which is always
   * `undefined` for a bare array. That silently returned `[]` on every call, and since nothing called
   * this method (confirmed by grep before this fix), it never surfaced. Also drops the `reps` ->
   * `maxRepsForWeight` remap that existed here: the backend never returned a `reps` field either, so
   * that transform was dead code operating on an already-empty array.
   */
  async getAllMaxWeights(userId?: string): Promise<ExercisePrRecord[]> {
    // Create cache key
    const cacheKey = userId || 'global';

    // Check cache first
    const cached = CacheManager.get(allMaxWeightsCache, cacheKey);
    if (cached !== null) {
      console.log(`📦 Using cached all max weights for user: ${cacheKey}`);
      return cached;
    }

    // Cache miss or expired, fetch from API
    console.log(`🌐 Fetching fresh all max weights for user: ${cacheKey}`);
    const url = userId ? `/workout-history/max-weights?userId=${userId}` : '/workout-history/max-weights';
    const result = (await this.get<ExercisePrRecord[]>(url)) || [];

    // Cache the result
    CacheManager.set(allMaxWeightsCache, cacheKey, result, CacheManager.ttl.maxWeight);

    return result;
  }

  /**
   * GET /api/workout-history/personal-records?userId={userId}&limit={limit}
   * Get top exerciseType-aware personal records with caching. Same wrapper-bug fix as
   * getAllMaxWeights above (TODO.md H4).
   */
  async getPersonalRecords(userId?: string, limit: number = 10): Promise<ExercisePrRecord[]> {
    // Create cache key
    const cacheKey = `${userId || 'global'}_${limit}`;

    // Check cache first
    const cached = CacheManager.get(personalRecordsCache, cacheKey);
    if (cached !== null) {
      console.log(`📦 Using cached personal records for: ${cacheKey}`);
      return cached;
    }

    // Cache miss or expired, fetch from API
    console.log(`🌐 Fetching fresh personal records for: ${cacheKey}`);
    const url = userId ? `/workout-history/personal-records?userId=${userId}&limit=${limit}` : `/workout-history/personal-records?limit=${limit}`;
    const result = (await this.get<ExercisePrRecord[]>(url)) || [];

    // Cache the result
    CacheManager.set(personalRecordsCache, cacheKey, result, CacheManager.ttl.maxWeight);

    return result;
  }

  /**
   * GET /api/workout-history/exercise-prs/:exerciseName?userId={userId}
   * TODO.md H4: what ExerciseInfoModal.tsx calls for its personal-records section. A new endpoint,
   * not a change to getMaxWeightForExercise below - that one has a live consumer (ExercisesPage.tsx /
   * ExerciseDetailModal.tsx) on a flat, weight-only shape that must not change in place. Returns `null`
   * (rather than throwing) both when the backend 404s (exercise never logged) and on any other
   * request failure, so the caller can just skip rendering the section instead of handling an error.
   */
  async getExercisePrRecord(userId: string, exerciseName: string): Promise<ExercisePrRecord | null> {
    const cacheKey = `${userId}_${exerciseName}`;

    const cached = CacheManager.get(exercisePrCache, cacheKey);
    if (cached !== null) {
      console.log(`📦 Using cached exercise PR record for: ${exerciseName}`);
      return cached;
    }

    console.log(`🌐 Fetching fresh exercise PR record for: ${exerciseName}`);
    let result: ExercisePrRecord | null;
    try {
      result = await this.get<ExercisePrRecord>(
        `/workout-history/exercise-prs/${encodeURIComponent(exerciseName)}?userId=${userId}`
      );
    } catch (error) {
      console.warn('Exercise PR record not found, treating as no history:', exerciseName, error);
      result = null;
    }

    // CacheManager.get() can't distinguish "cached null" from "no entry" (both return null), so only
    // cache a real record - a not-found result just re-fetches next time, which is cheap and correct.
    if (result !== null) {
      CacheManager.set(exercisePrCache, cacheKey, result, CacheManager.ttl.maxWeight);
    }

    return result;
  }

  /**
   * Clear all max weight related caches
   * Call this when a workout is saved or ended
   */
  clearMaxWeightCache(): void {
    console.log('🧹 Clearing max weight caches');
    CacheManager.clear(maxWeightCache);
    CacheManager.clear(allMaxWeightsCache);
    CacheManager.clear(personalRecordsCache);
    CacheManager.clear(exercisePrCache);
  }

  /**
   * Clear progress analytics cache
   * Call this when a workout is saved or ended
   */
  clearProgressAnalyticsCache(): void {
    console.log('🧹 Clearing progress analytics cache');
    CacheManager.clear(progressAnalyticsCache);
  }

  /**
   * Clear all caches (max weights + progress analytics)
   * Call this when a workout is saved or ended
   */
  clearAllCaches(): void {
    console.log('🧹 Clearing all workout history caches');
    this.clearMaxWeightCache();
    this.clearProgressAnalyticsCache();
  }

  /**
   * Clear max weight cache for a specific user
   * Call this when a specific user's workout is saved or ended
   */
  clearMaxWeightCacheForUser(userId: string): void {
    console.log(`🧹 Clearing max weight cache for user: ${userId}`);
    
    // Clear individual max weight caches for this user
    Array.from(maxWeightCache.keys()).forEach(key => {
      if (key.startsWith(`${userId}_`)) {
        CacheManager.delete(maxWeightCache, key);
      }
    });
    
    // Clear all max weights cache for this user
    CacheManager.delete(allMaxWeightsCache, userId);
    
    // Clear personal records cache for this user
    Array.from(personalRecordsCache.keys()).forEach(key => {
      if (key.startsWith(`${userId}_`)) {
        CacheManager.delete(personalRecordsCache, key);
      }
    });

    // Clear exercise PR record cache for this user (TODO.md H4)
    Array.from(exercisePrCache.keys()).forEach(key => {
      if (key.startsWith(`${userId}_`)) {
        CacheManager.delete(exercisePrCache, key);
      }
    });
  }

  /**
   * Clear progress analytics cache for a specific user
   * Call this when a specific user's workout is saved or ended
   */
  clearProgressAnalyticsCacheForUser(userId: string): void {
    console.log(`🧹 Clearing progress analytics cache for user: ${userId}`);
    
    // Clear progress analytics caches for this user
    Array.from(progressAnalyticsCache.keys()).forEach(key => {
      if (key.startsWith(`${userId}_`)) {
        CacheManager.delete(progressAnalyticsCache, key);
      }
    });
  }

  /**
   * Clear all caches for a specific user
   * Call this when a specific user's workout is saved or ended
   */
  clearAllCachesForUser(userId: string): void {
    console.log(`🧹 Clearing all caches for user: ${userId}`);
    this.clearMaxWeightCacheForUser(userId);
    this.clearProgressAnalyticsCacheForUser(userId);
    // Clear history cache entries for this user
    Array.from(historyCache.keys()).forEach(key => {
      if (key.startsWith(`${userId}_`)) {
        CacheManager.delete(historyCache, key);
      }
    });
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): {
    individualMaxWeights: number;
    allMaxWeights: number;
    personalRecords: number;
    progressAnalytics: number;
    total: number;
  } {
    return {
      individualMaxWeights: maxWeightCache.size,
      allMaxWeights: allMaxWeightsCache.size,
      personalRecords: personalRecordsCache.size,
      progressAnalytics: progressAnalyticsCache.size,
      total: maxWeightCache.size + allMaxWeightsCache.size + personalRecordsCache.size + progressAnalyticsCache.size
    };
  }

  // Keep the old method for backward compatibility
  getMaxWeightCacheStats(): {
    individualMaxWeights: number;
    allMaxWeights: number;
    personalRecords: number;
    total: number;
  } {
    return {
      individualMaxWeights: maxWeightCache.size,
      allMaxWeights: allMaxWeightsCache.size,
      personalRecords: personalRecordsCache.size,
      total: maxWeightCache.size + allMaxWeightsCache.size + personalRecordsCache.size
    };
  }

  /**
   * Get progress analytics data with caching
   * This includes chart data, top exercises, and muscle distribution
   */
  async getProgressAnalytics(
    userId: string, 
    timeRangeDays: number = 90
  ): Promise<{
    chartData: any[];
    topExercises: any[];
    seriesKeys: string[];
    lineData: any[];
    pieData: any[];
  }> {
    // Create cache key based on user and time range
    const cacheKey = `${userId}_${timeRangeDays}`;
    
    // Check cache first
    const cached = CacheManager.get(progressAnalyticsCache, cacheKey);
    if (cached !== null) {
      console.log(`📦 Using cached progress analytics for user: ${userId}, range: ${timeRangeDays} days`);
      return cached;
    }

    // Cache miss or expired, fetch and process data
    console.log(`🌐 Computing fresh progress analytics for user: ${userId}, range: ${timeRangeDays} days`);
    
    try {
      // Fetch workout history
      const historyResponse = await this.getWorkoutHistory(userId, 1, 1000);
      const history = historyResponse.data;

      if (!history || history.length === 0) {
        const emptyResult = {
          chartData: [],
          topExercises: [],
          seriesKeys: [],
          lineData: [],
          pieData: []
        };
        
        // Cache empty result for shorter time
        CacheManager.set(progressAnalyticsCache, cacheKey, emptyResult, CacheManager.ttl.progressAnalyticsEmpty);
        
        return emptyResult;
      }

      // Filter by time range
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - timeRangeDays);
      const filtered = history.filter(w => {
        const workoutDate = typeof w.date === 'string' ? new Date(w.date) : w.date;
        return workoutDate >= cutoffDate;
      });

      // Sort ascending by date
      const sorted = [...filtered].sort((a, b) => {
        const dateA = typeof a.date === 'string' ? new Date(a.date) : a.date;
        const dateB = typeof b.date === 'string' ? new Date(b.date) : b.date;
        return dateA.getTime() - dateB.getTime();
      });

      // Import ExerciseService for exercise lookups
      const { ExerciseService } = await import('./ExerciseService');
      const exerciseService = new ExerciseService();

      // Build muscle distribution pie data
      const muscleCounts = new Map<string, number>();
      for (const w of sorted) {
        for (const ex of w.exercises) {
          const def = await exerciseService.findExerciseByName(ex.name);
          const muscle = def?.muscleGroup;
          if (!muscle) continue;
          muscleCounts.set(muscle, (muscleCounts.get(muscle) || 0) + 1);
        }
      }

      const COLORS: Record<string, string> = {
        chest: '#E91E63',
        back: '#2196F3',
        shoulders: '#FF9800',
        biceps: '#9C27B0',
        triceps: '#00BCD4',
        arms: '#8BC34A',
        legs: '#4CAF50',
        abs: '#FFC107',
      };

      const pieData = Array.from(muscleCounts.entries())
        .map(([name, value]) => ({ name, value, color: COLORS[name] || '#8884d8' }))
        .sort((a, b) => b.value - a.value);

      // Build top exercises data
      const exerciseCounts = new Map<string, { count: number; lastUsed: Date }>();
      for (const w of sorted) {
        for (const ex of w.exercises) {
          const existing = exerciseCounts.get(ex.name);
          const workoutDate = typeof w.date === 'string' ? new Date(w.date) : w.date;
          if (existing) {
            existing.count++;
            if (workoutDate > existing.lastUsed) {
              existing.lastUsed = workoutDate;
            }
          } else {
            exerciseCounts.set(ex.name, { count: 1, lastUsed: workoutDate });
          }
        }
      }

      const topExercises = Array.from(exerciseCounts.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Build chart data (simplified for caching)
      const chartData = sorted.map(w => ({
        date: w.date,
        exerciseCount: w.exercises.length,
        totalReps: w.exercises.reduce((sum, ex) => sum + ex.sets.reduce((setSum, set) => setSum + set.reps, 0), 0)
      }));

      // Build line data for muscle groups over time
      const muscleGroupData = new Map<string, Array<{ date: string; value: number }>>();
      for (const w of sorted) {
        const dateStr = w.date instanceof Date ? w.date.toISOString().split('T')[0] : (w.date as string).split('T')[0];
        
        for (const ex of w.exercises) {
          const def = await exerciseService.findExerciseByName(ex.name);
          const muscle = def?.muscleGroup;
          if (!muscle) continue;
          
          if (!muscleGroupData.has(muscle)) {
            muscleGroupData.set(muscle, []);
          }
          
          const existing = muscleGroupData.get(muscle)!.find(d => d.date === dateStr);
          if (existing) {
            existing.value += 1;
          } else {
            muscleGroupData.get(muscle)!.push({ date: dateStr, value: 1 });
          }
        }
      }

      const seriesKeys = Array.from(muscleGroupData.keys());
      const lineData = chartData.map(d => {
        const entry: any = { date: d.date };
        seriesKeys.forEach(key => {
          const muscleData = muscleGroupData.get(key);
          const chartDateStr = d.date instanceof Date ? d.date.toISOString().split('T')[0] : (d.date as string).split('T')[0];
          entry[key] = muscleData?.find(m => m.date === chartDateStr)?.value || 0;
        });
        return entry;
      });

      const result = {
        chartData,
        topExercises,
        seriesKeys,
        lineData,
        pieData
      };

      // Cache the result
      CacheManager.set(progressAnalyticsCache, cacheKey, result, CacheManager.ttl.progressAnalytics);

      return result;

    } catch (error) {
      console.error('Error computing progress analytics:', error);
      throw error;
    }
  }
}
